import { personalTimeFormat } from './timePrefs.js';
import { isHolidayOn } from '@ehr/shared';
import type {
  HolidayDates,
  HoursDay,
  HoursMode,
  HoursWeek,
  Pay,
  TimeOff,
  Timesheet,
  TimesheetEntry,
  UserHours,
} from '@ehr/shared';

/**
 * User hours (ehr#60, the CRM "User Operational Hours" editor): defaults, weekly totals
 * and the live status pill. Days are keyed '0' (Sunday) … '6'; the editor shows Monday
 * first. "Now" is evaluated in the practice time zone when one is set.
 */

export type DayKey = '0' | '1' | '2' | '3' | '4' | '5' | '6';
export const DAY_ORDER: DayKey[] = ['1', '2', '3', '4', '5', '6', '0'];
export const DAY_NAMES: Record<DayKey, string> = {
  '0': 'Sunday',
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
};
export const MODE_LABELS: Record<HoursMode, string> = {
  operational: 'Operational Hours',
  afterHours: 'After Hours',
  splitShift: 'Split Shift',
};

const emptyRange = () => ({ from: '', to: '' });

function week(
  on: (d: DayKey) => boolean,
  shifts: { from: string; to: string }[],
  withBreaks: boolean,
): HoursWeek {
  const out = {} as HoursWeek;
  for (const d of DAY_ORDER) {
    out[d] = {
      on: on(d),
      shifts: shifts.map((s) => ({ ...s })),
      breaks: withBreaks ? [emptyRange(), emptyRange()] : [],
      meal: withBreaks ? emptyRange() : null,
    };
  }
  return out;
}

/** Mockup defaults (defaultUserHoursModes): Mon–Fri 9–5; after hours and split shift start off. */
export function defaultHours(): UserHours {
  return {
    modes: {
      operational: week((d) => d !== '0' && d !== '6', [{ from: '09:00', to: '17:00' }], true),
      afterHours: week(() => false, [{ from: '17:00', to: '21:00' }], true),
      splitShift: week(
        () => false,
        [
          { from: '08:00', to: '12:00' },
          { from: '13:00', to: '18:00' },
        ],
        true,
      ),
    },
    effectiveDate: '',
    timeOff: [],
  };
}

const mins = (hhmm: string) => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
};
const span = (r: { from: string; to: string }) => {
  const v = mins(r.to) - mins(r.from);
  return Number.isFinite(v) && v > 0 ? v : 0;
};

/** Working minutes in a day: shifts minus breaks and the meal. */
export function dayMinutes(day: HoursDay): number {
  if (!day.on) return 0;
  const work = day.shifts.reduce((a, s) => a + span(s), 0);
  const off = day.breaks.reduce((a, b) => a + span(b), 0) + (day.meal ? span(day.meal) : 0);
  return Math.max(0, work - off);
}

export function weekSummary(w: HoursWeek): { days: number; hours: number } {
  let days = 0;
  let total = 0;
  for (const d of DAY_ORDER) {
    if (w[d].on) days++;
    total += dayMinutes(w[d]);
  }
  return { days, hours: Math.round((total / 60) * 10) / 10 };
}

/** First invalid range in a mode, as a readable message, or null. */
export function firstHoursError(h: UserHours): string | null {
  for (const mode of Object.keys(MODE_LABELS) as HoursMode[]) {
    for (const d of DAY_ORDER) {
      const day = h.modes[mode][d];
      if (!day.on) continue;
      const check = (r: { from: string; to: string }, what: string) => {
        if (!r.from && !r.to) return null;
        if (!r.from || !r.to)
          return `${MODE_LABELS[mode]} · ${DAY_NAMES[d]}: ${what} needs both times`;
        if (mins(r.to) <= mins(r.from))
          return `${MODE_LABELS[mode]} · ${DAY_NAMES[d]}: ${what} end must be after start`;
        return null;
      };
      for (const s of day.shifts) {
        if (!s.from || !s.to)
          return `${MODE_LABELS[mode]} · ${DAY_NAMES[d]}: shift needs both times`;
        const e = check(s, 'shift');
        if (e) return e;
      }
      for (const b of day.breaks) {
        const e = check(b, 'break');
        if (e) return e;
      }
      if (day.meal) {
        const e = check(day.meal, 'meal');
        if (e) return e;
      }
    }
  }
  return null;
}

/** 'Pacific — America/Los_Angeles' (the practice profile's stored form) → IANA name. */
export function ianaZone(tz: string | null | undefined): string | undefined {
  const name = (tz ?? '').split('—').pop()?.trim();
  if (!name) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return name;
  } catch {
    return undefined;
  }
}

/** Date (YYYY-MM-DD), weekday and HH:MM of `at` in the zone (device zone when none). */
export function zonedNow(at: Date, tz?: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    day: String(Math.max(0, dow)) as DayKey,
    time: `${get('hour').replace('24', '00')}:${get('minute')}`,
  };
}

export type StatusTone = 'green' | 'amber' | 'red' | 'grey';
export interface UserStatus {
  tone: StatusTone;
  short: string;
  full: string;
}

/** CRM userScheduleStatus order: holiday → time off → no hours → pending → off today → available → off-hours. */
export function userStatus(
  hours: UserHours | null | undefined,
  holidays: (HolidayDates & { name: string })[],
  tz?: string,
  at: Date = new Date(),
): UserStatus {
  const now = zonedNow(at, tz);
  // ehr#376: a holiday may repeat (every January 1, the fourth Thursday in November)
  const hol = holidays.find((h) => isHolidayOn(h, now.date));
  if (hol) return { tone: 'red', short: 'Holiday', full: `Company holiday · ${hol.name}` };
  const off = activeTimeOff(hours?.timeOff ?? [], now.date);
  if (off)
    return {
      tone: 'amber',
      short: off.type,
      full: `On ${off.type.toLowerCase()} · back ${fmtMonthDay(addDays(off.end, 1))}`,
    };
  if (!hours?.modes) return { tone: 'grey', short: 'No hours', full: 'No operational hours set' };
  if (hours.effectiveDate && now.date < hours.effectiveDate)
    return {
      tone: 'grey',
      short: 'Pending',
      full: `Schedule starts ${fmtDate(hours.effectiveDate)}`,
    };
  const days = (Object.keys(MODE_LABELS) as HoursMode[]).map((m) => hours.modes[m]?.[now.day]);
  const onToday = days.filter((d): d is HoursDay => !!d?.on);
  if (!onToday.length) return { tone: 'grey', short: 'Off today', full: 'Not scheduled today' };
  const inShift = onToday.some((d) => d.shifts.some((s) => s.from <= now.time && now.time < s.to));
  return inShift
    ? { tone: 'green', short: 'Available', full: 'Available now' }
    : { tone: 'grey', short: 'Off-hours', full: 'Outside scheduled hours right now' };
}

/** 'YYYY-MM-DD' → 'Oct 15, 2026'. */
export function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** 'HH:MM' (24h) → '09:00 AM' (mockup uhFmt24to12); '' when unset. */
export function fmt12(v: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v ?? '');
  if (!m) return '';
  let h = Number(m[1]);
  const mer = h >= 12 ? 'PM' : 'AM';
  if (h > 12) h -= 12;
  if (h === 0) h = 12;
  return `${String(h).padStart(2, '0')}:${m[2]} ${mer}`;
}

/**
 * Typed time → 'HH:MM' (mockup uhParse12): accepts "9", "9a", "930pm", "9:30 PM",
 * "14:00". Null when it can't be read.
 */
export function parse12(raw: string): string | null {
  let s = String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9APM:]/g, '');
  const mer = s.includes('P') ? 'PM' : s.includes('A') ? 'AM' : null;
  s = s.replace(/[APM]/g, '');
  let h: number;
  let m = 0;
  const colon = s.indexOf(':');
  if (colon >= 0) {
    // A colon says where the hour ends: "1:3" is 1:03, not 13:00 (ehr#60 time mask).
    const hs = s.slice(0, colon);
    const ms = s.slice(colon + 1).replace(/:/g, '');
    if (!hs.length || hs.length > 2 || ms.length > 2) return null;
    h = Number(hs);
    m = ms ? Number(ms) : 0;
  } else {
    if (!s.length || s.length > 4) return null;
    if (s.length <= 2) h = Number(s);
    else if (s.length === 3) {
      h = Number(s.slice(0, 1));
      m = Number(s.slice(1));
    } else {
      h = Number(s.slice(0, 2));
      m = Number(s.slice(2, 4));
    }
  }
  if (!Number.isFinite(h) || !Number.isFinite(m) || m > 59) return null;
  if (mer && (h < 1 || h > 12)) return null;
  if (mer === 'PM' && h !== 12) h += 12;
  if (mer === 'AM' && h === 12) h = 0;
  if (h > 23 || h < 0) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * ehr#60 - as-you-type mask for a time box, so it only ever holds a time shape like
 * "9:30 PM": letters other than A/P/M are dropped, the colon goes in by itself ("9" →
 * "9:", "12" → "12:"), minutes stop at two digits (first 0-5), and typing a or p adds
 * " AM" / " PM". 24-hour entry still works ("14:00"). Deleting is never fought: when the
 * text got shorter it is only cleaned, not re-completed. `prev` is the box's last text.
 */
export function maskTime(raw: string, prev = ''): string {
  const up = String(raw ?? '')
    .toUpperCase()
    .replace(/[^0-9APM: ]/g, '');
  if (up.length < prev.length) return up.slice(0, 8);
  const digits = up.replace(/\D/g, '');
  if (!digits) return '';
  const mer = (/[AP]/.exec(up) ?? [''])[0];
  const colonAt = up.indexOf(':');
  const d0 = digits[0]!;
  const d1 = digits[1];
  // Hour length: 3-9 are one digit; 0x / 1x two; 2 then 0-3 two (20-23), else one.
  let hLen = d0 >= '3' || d1 === undefined ? 1 : d0 === '2' ? (d1 <= '3' ? 2 : 1) : 2;
  if (colonAt >= 0) {
    const before = up.slice(0, colonAt).replace(/\D/g, '');
    if (before.length === 1 || before.length === 2) hLen = before.length;
  }
  const hour = digits.slice(0, hLen);
  let mins = '';
  for (const c of digits.slice(hLen)) {
    if (mins.length === 2 || (mins.length === 0 && c > '5')) break;
    mins += c;
  }
  // The hour is finished once it can't grow: two digits, a 3-9 start, a typed colon,
  // or more digits after it.
  const hourDone = hLen === 2 || d0 >= '3' || colonAt >= 0 || digits.length > hLen;
  let out = hour + (hourDone ? ':' : '') + mins;
  const h = Number(hour);
  if (mer && h >= 1 && h <= 12) out = out.replace(/:$/, '') + ' ' + mer + 'M';
  return out;
}

// ── Time Off / Pay / Timesheet tabs (mockup uhAddTimeOff / uhComputeComp / ts*) ──
const pad2 = (n: number) => String(n).padStart(2, '0');

/** Local calendar date of `d` as 'YYYY-MM-DD'. */
export function localDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
/** Local time of `d` as 'HH:MM' (24h). */
export function localHhmm(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
const parseIso = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
};
/** 'YYYY-MM-DD' shifted by `n` days. */
export function addDays(iso: string, n: number): string {
  const d = parseIso(iso);
  d.setDate(d.getDate() + n);
  return localDate(d);
}
/** 'YYYY-MM-DD' → 'Oct 3'. */
export function fmtMonthDay(iso: string): string {
  return parseIso(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** The first non-denied time-off entry covering `date`, if any. */
export function activeTimeOff(list: TimeOff[], date: string): TimeOff | undefined {
  return list.find((t) => t.status !== 'Denied' && t.start <= date && date <= t.end);
}

export interface TimeOffDraft {
  start: string;
  end: string;
  type: TimeOff['type'];
  note: string;
}
/** Why a new time-off draft can't be added (mockup uhAddTimeOff), or null. */
export function timeOffDraftError(d: TimeOffDraft): string | null {
  if (!d.start || !d.end) return 'Pick a start and end date';
  if (d.end < d.start) return 'End date must be on or after the start date';
  return null;
}
/** Adds a Pending entry and keeps the list sorted by start date. */
export function addTimeOff(list: TimeOff[], d: TimeOffDraft): TimeOff[] {
  const next: TimeOff[] = [
    ...list,
    { start: d.start, end: d.end, type: d.type, note: d.note.trim(), status: 'Pending' },
  ];
  return next.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
}

export function defaultPay(): Pay {
  return {
    basis: 'hourly',
    hourly: '',
    daily: '',
    frequency: 'biweekly',
    otHours: '',
    otMult: 1.5,
    bonuses: [],
  };
}
export function defaultTimesheet(): Timesheet {
  return { open: null, entries: [] };
}

export const PAY_FREQ_LABEL: Record<Pay['frequency'], string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
};
/** The compensation card's period: "per 2 weeks" etc. */
export const PAY_PERIOD_LABEL: Record<Pay['frequency'], string> = {
  daily: 'per day',
  weekly: 'per week',
  biweekly: 'per 2 weeks',
  monthly: 'per month',
};

export interface Compensation {
  /** Scheduled hours / week across all three schedules. */
  weeklyH: number;
  /** Distinct weekdays scheduled in any schedule. */
  days: number;
  weeklyBase: number;
  periodBase: number;
  ot: number;
  bonus: number;
  bonusPct: number;
  total: number;
}

/**
 * Mockup uhComputeComp: scheduled hours / week (all schedules), base pay per period
 * (hourly × hours, or daily × days), overtime = OT hours × hourly × multiplier, and
 * the bonus % of the last tier whose [min, max] contains the weekly hours.
 */
export function computeComp(h: UserHours): Compensation {
  const pay = h.pay ?? defaultPay();
  let minutes = 0;
  const onDays = new Set<DayKey>();
  for (const m of Object.keys(MODE_LABELS) as HoursMode[])
    for (const d of DAY_ORDER) {
      const day = h.modes[m][d];
      minutes += dayMinutes(day);
      if (day.on) onDays.add(d);
    }
  const weeklyH = minutes / 60;
  const days = onDays.size;
  const num = (v: number | '') => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const hourly = num(pay.hourly);
  const otMult = num(pay.otMult) || 1.5;
  const weeklyBase = pay.basis === 'daily' ? num(pay.daily) * days : hourly * weeklyH;
  const factors: Record<Pay['frequency'], number> = {
    daily: days > 0 ? 1 / days : 0,
    weekly: 1,
    biweekly: 2,
    monthly: 52 / 12,
  };
  const periodBase = weeklyBase * factors[pay.frequency];
  const ot = num(pay.otHours) * hourly * otMult;
  let bonusPct = 0;
  for (const b of pay.bonuses) {
    if (b.pct === '') continue;
    const lo = b.min === '' ? -Infinity : b.min;
    const hi = b.max === '' ? Infinity : b.max;
    if (weeklyH >= lo && weeklyH <= hi) bonusPct = b.pct;
  }
  const bonus = periodBase * (bonusPct / 100);
  return {
    weeklyH,
    days,
    weeklyBase,
    periodBase,
    ot,
    bonus,
    bonusPct,
    total: periodBase + ot + bonus,
  };
}

/** 1234.5 → '$1,234.50' (mockup uhMoney). */
export function fmtMoney(n: number): string {
  const v = Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Sorted by date then start time (mockup ts* push + sort). */
export function sortEntries(list: TimesheetEntry[]): TimesheetEntry[] {
  return [...list].sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start));
}

/** Mockup tsClockOut: the entry for a shift clocked in at `startIso`, out at `end`. */
export function clockOutEntry(startIso: string, end: Date): TimesheetEntry {
  const start = new Date(startIso);
  const hrs = Math.min(24, Math.max(0, (end.getTime() - start.getTime()) / 3_600_000));
  return {
    date: localDate(start),
    start: localHhmm(start),
    end: localHhmm(end),
    hours: round2(hrs),
    note: '',
    source: 'clock',
  };
}

/** Hours logged on one day. */
export function dayTotal(entries: TimesheetEntry[], date: string): number {
  return entries.reduce((a, e) => a + (e.date === date ? e.hours : 0), 0);
}
/** Hours logged in a month ('YYYY-MM'). */
export function monthTotal(entries: TimesheetEntry[], month: string): number {
  return entries.reduce((a, e) => a + (e.date.slice(0, 7) === month ? e.hours : 0), 0);
}
/** Hours logged in the Sunday–Saturday week containing `date`. */
export function weekTotal(entries: TimesheetEntry[], date: string): number {
  const sun = addDays(date, -parseIso(date).getDay());
  const sat = addDays(sun, 6);
  return entries.reduce((a, e) => a + (e.date >= sun && e.date <= sat ? e.hours : 0), 0);
}

/** 'YYYY-MM' shifted by `delta` months. */
export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y ?? 1970, (m ?? 1) - 1 + delta, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}
/** 'YYYY-MM' → 'October 2026'. */
export function fmtMonth(month: string): string {
  return parseIso(`${month}-01`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
/** 'YYYY-MM-DD' → 'Sat, Oct 3'. */
export function fmtDayLabel(iso: string): string {
  return parseIso(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}
/** Month calendar: blank leading cells (Sunday first) and each day as 'YYYY-MM-DD'. */
export function monthGrid(month: string): { lead: number; days: string[] } {
  const first = parseIso(`${month}-01`);
  const count = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const days = Array.from({ length: count }, (_, i) => `${month}-${pad2(i + 1)}`);
  return { lead: first.getDay(), days };
}

/** 1.2 hours → '1h 12m' (mockup tsFmtElapsed). */
export function fmtElapsed(hours: number): string {
  const total = Math.max(0, Math.round(hours * 60));
  return `${Math.floor(total / 60)}h ${pad2(total % 60)}m`;
}
/** 'HH:MM' → '9:05 AM', or '09:05' for a person on 24-hour (ehr#407). */
export function fmtClock(hhmm: string): string {
  if (personalTimeFormat() === '24h') {
    const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm ?? '');
    return m ? `${m[1]!.padStart(2, '0')}:${m[2]}` : '';
  }
  return fmt12(hhmm).replace(/^0/, '');
}
